/**
 * translatedOverlayUnderline.tsx
 *
 * Shared render-time overlay-underline algorithm for the two translated
 * transcript views (`TranscriptionTextTranslated` regular + `TranscriptionDiffTranslated`
 * redline). Both render one `Word` span per phrase with bare inter-phrase
 * whitespace between them, so the imperative per-span `overlay-match` underline
 * left a GAP at every inter-phrase space (and broke across struck words in the
 * redline). The translated screens therefore need their own underline
 * algorithm, distinct from the per-word original-language views.
 *
 * `renderPhrasesWithUnderline` wraps each run of consecutive phrases sharing one
 * overlay id — plus the whitespace between them — in a single `OverlayUnderline`
 * span, so the navy underline is continuous across the run and rides through
 * struck words (whose red strikethrough is owned by the inner `Word` and is not
 * recolored). The space BEFORE a run stays outside the underline (it connects to
 * an out-of-range phrase). The `data-overlay-id` span is what hover/selection
 * brightens via `brightenOverlayUnderlinesById`.
 */

import { OVERLAY_MATCH_ACTIVE_CLASS } from '@src/utility/transcriptQuoteUtils';
import React from 'react';
import styled from 'styled-components';

import { needsSpaceBetweenPhrases } from './utils';

import type { TranslatedSortedPhrase } from './hooks/useTranslatedRender';
import type { HighlightRange } from '@src/utility/transcriptQuoteUtils';

const OverlayUnderline = styled.span`
  text-decoration: underline;
  text-decoration-color: var(--content-overlays);
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
  text-decoration-skip-ink: none;
  &.${OVERLAY_MATCH_ACTIVE_CLASS} {
    text-decoration-color: var(--content-overlays-hover);
  }
`;

export function renderPhrasesWithUnderline(
  phrases: TranslatedSortedPhrase[],
  overlayRanges: HighlightRange[] | undefined,
  renderOne: (p: TranslatedSortedPhrase, pi: number) => React.ReactNode,
): React.ReactNode[] {
  const overlayIdForLead = (lead: number): string | null =>
    overlayRanges?.find(r => lead >= r.start && lead <= r.end)?.id ?? null;
  // Inter-phrase space the data omits (phrase values carry no whitespace),
  // punctuation-aware so we don't space before commas/periods.
  const spaceBefore = (pi: number): string | null =>
    pi > 0 &&
    needsSpaceBetweenPhrases(
      phrases[pi - 1].phrase.translatedValue ?? '',
      phrases[pi].phrase.translatedValue ?? '',
    )
      ? ' '
      : null;

  const nodes: React.ReactNode[] = [];
  let i = 0;
  while (i < phrases.length) {
    const overlayId = overlayIdForLead(phrases[i].lead);
    if (overlayId == null) {
      const pi = i;
      nodes.push(
        <React.Fragment key={`pf-${phrases[pi].lead}-${pi}`}>
          {spaceBefore(pi)}
          {renderOne(phrases[pi], pi)}
        </React.Fragment>,
      );
      i++;
      continue;
    }
    // Run of consecutive phrases sharing one overlay id. The space BEFORE the
    // run stays outside the underline; spaces BETWEEN run members go inside so
    // the underline is continuous.
    const runStart = i;
    const inner: React.ReactNode[] = [];
    while (
      i < phrases.length &&
      overlayIdForLead(phrases[i].lead) === overlayId
    ) {
      const pi = i;
      inner.push(
        <React.Fragment key={`pf-${phrases[pi].lead}-${pi}`}>
          {pi > runStart ? spaceBefore(pi) : null}
          {renderOne(phrases[pi], pi)}
        </React.Fragment>,
      );
      i++;
    }
    nodes.push(
      <React.Fragment key={`run-${runStart}`}>
        {spaceBefore(runStart)}
        <OverlayUnderline data-overlay-id={overlayId}>{inner}</OverlayUnderline>
      </React.Fragment>,
    );
  }
  return nodes;
}
